import { memo, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedCampaign } from '../../../../../store/campaignsStore';
import { isLevelUnlocked, isRebateSource } from '../../../../../utils/variants/rebateHelpers';
import { useCurrency } from '../../../../../../../store/app/appStore';
import CampaignTable from '../../../../shared/CampaignTable';
import type { IUserProgress } from '../../../../../types/campaign.types';
import Image from '../../../../../../../shared/components/Image';

import styles from './WinningsTab.module.css';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';
import RebateProgressItem from '../../RebateProgressItem/RebateProgressItem';

const WIN_DEFAULT_VALUE = '0.00';

const TABLE_COLUMNS = [
  {
    name: 'Levels',
    key: 'rebate.levels',
    cellClassName: '',
  },
  {
    name: 'Rebate',
    key: 'rebate.rebate',
    className: '',
  },
  {
    name: 'Max Win',
    key: 'rebate.max_win',
    className: '',
  },
];

interface OverviewContainerProps {
  userProgress: IUserProgress | null;
  camapaignCurrency: string;
}

const OverviewContainer: React.FC<OverviewContainerProps> = ({ userProgress, camapaignCurrency }) => {
  const { t } = useTranslation();
  const totalWin = userProgress ? userProgress.totalWinAmount.toFixed(2) : WIN_DEFAULT_VALUE;
  const claimedWin = userProgress ? userProgress.claimedWin.toFixed(2) : WIN_DEFAULT_VALUE;

  return (
    <div className={styles.overviewContainer}>
      <div className={`${styles.overviewContainerCell} ${styles.left}`}>
        <span className={styles.overviewCellHeading}>{t('campaigns.total_win', 'Total Win')}</span>
        <span className={styles.overviewCellData}>{totalWin}<span className={styles.overviewCellCurrency}>{camapaignCurrency}</span></span>
      </div>
      <div className={`${styles.overviewContainerCell} ${styles.right}`}>
        <span className={styles.overviewCellHeading}>{t('campaigns.claimed_win', 'Claimed Win')}</span>
        <span className={styles.overviewCellData}>{claimedWin}<span className={styles.overviewCellCurrency}>{camapaignCurrency}</span></span>
      </div>
    </div>
  );
};

interface LevelIconProps {
  unlocked: boolean
}

const LevelIcon: React.FC<LevelIconProps> = ({ unlocked }) => {
  return (
    <Image
      src={`/campaigns/icons/level${unlocked ? 'Unlock' : 'Lock'}`}
      manualAffix=".svg"
      className={`${styles.levelIcon} ${unlocked ? styles.levelIconUnlocked : ''}`}
    />
  );
};

const WinningsTab: React.FC = () => {
  const selectedCampaign = useSelectedCampaign();
  const currency = useCurrency();
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [progressItemWidth, setProgressItemWidth] = useState<number | undefined>(undefined);

  const cashbackLevels = selectedCampaign && isRebateSource(selectedCampaign) ? selectedCampaign.cashbackLevels : undefined;

  useLayoutEffect(() => {
    const container = wrapperRef.current;
    if (!container || !cashbackLevels || cashbackLevels.length === 0) return undefined;

    const measure = () => {
      const maxWinEls = container.querySelectorAll<HTMLElement>(`.${styles.maxWin}`);
      let maxWidth = 0;
      maxWinEls.forEach((el) => {
        maxWidth = Math.max(maxWidth, el.getBoundingClientRect().width);
      });
      if (maxWidth > 0) setProgressItemWidth(maxWidth);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    container.querySelectorAll<HTMLElement>(`.${styles.maxWin}`).forEach((el) => resizeObserver.observe(el));

    return () => resizeObserver.disconnect();
  }, [cashbackLevels]);

  if (!selectedCampaign || !isRebateSource(selectedCampaign)) return null;

  const camapaignCurrency = selectedCampaign.baseCurrency || currency;
  const userProgress = selectedCampaign.userProgress;
  const spentBetAmount = userProgress?.spentBetAmount;
  const campaignMinWinBetAmount = selectedCampaign.minWinBetAmount;

  return (
    <div
      className={styles.winningsTabWrapper}
      ref={wrapperRef}
      style={progressItemWidth ? ({ '--progress-item-width': `${progressItemWidth}px` } as React.CSSProperties) : undefined}
    >
      <OverviewContainer userProgress={userProgress} camapaignCurrency={camapaignCurrency} />
      <CampaignTable columns={TABLE_COLUMNS} tableType='winnings' className='rebate-winnings'>
        {selectedCampaign.cashbackLevels.map((level) => {
          const levelNumber = level.levelNumber;
          const betRangeFrom = level.betRangeFrom;
          const betRangeTo = level.betRangeTo;
          const rebatePercent = level.rebatePercent.toFixed(1);
          const isCurrentLevelUnlocked = isLevelUnlocked(spentBetAmount, campaignMinWinBetAmount, level);
          const maxWin = level.maxPayoutAmount.toFixed(2);

          return (
            <tr key={`level-${levelNumber}`} className={`${tableStyles.tableRow} ${styles.tableRow}`}>
              <td className={`${tableStyles.tableCell} ${styles.levelsCellContainer}`}>
                <span className={styles.levelsCellTop}>
                  {`${t('rebate.level', 'Level')} ${levelNumber}`}
                </span>
                <span className={styles.levelsCellBottom}>
                  {`${betRangeFrom} - ${betRangeTo} ${camapaignCurrency}`}
                </span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.percentageCell}`}>
                <LevelIcon unlocked={isCurrentLevelUnlocked} />
                <span className={styles.percentage}>{rebatePercent}%</span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.maxWinCell}`}>
                <div className={styles.maxWin}>+ {maxWin}<span>{camapaignCurrency}</span></div>
                <RebateProgressItem
                  cashbackLevel={level}
                  userProgress={userProgress}
                  minWinBetAmount={campaignMinWinBetAmount}
                  variant='winnings-tab'
                />
              </td>
            </tr>
          );
        })}
      </CampaignTable>
    </div>
  );
};

export default memo(WinningsTab);
